import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Users2, ArrowRight, Trash2, PhoneCall } from "lucide-react";
import { listFarmerInterests, convertInterestToProfile, updateFarmerInterest, deleteFarmerInterest } from "@/lib/api";

const STATUS_TONES = {
  new:       "bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)]",
  contacted: "bg-[var(--tr-zest)]/20 text-[var(--tr-zest)]",
  converted: "bg-[var(--tr-lime)]/30 text-[var(--tr-deep-green)]",
  rejected:  "bg-red-100 text-red-700",
};

export default function AdminFarmerInterests() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");

  const load = () => {
    listFarmerInterests(status ? { status } : {}).then(setItems).catch(() => setItems([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const convert = async (id) => {
    if (!window.confirm(t("admin_p2.convert_confirm"))) return;
    try {
      const r = await convertInterestToProfile(id);
      toast.success(`${t("admin_p2.converted")}: ${r.profile.farmer_code}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || t("toast.failed"));
    }
  };

  const markContacted = async (id) => {
    try {
      await updateFarmerInterest(id, { status: "contacted" });
      toast.success("Marked contacted");
      load();
    } catch { toast.error(t("toast.failed")); }
  };

  const reject = async (id) => {
    if (!window.confirm(t("admin_p2.reject_confirm"))) return;
    try {
      await updateFarmerInterest(id, { status: "rejected" });
      toast.success(t("toast.deleted"));
      load();
    } catch { toast.error(t("toast.failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    await deleteFarmerInterest(id);
    toast.success(t("toast.deleted"));
    load();
  };

  return (
    <div data-testid="admin-farmer-interests">
      <div className="flex items-center gap-3 mb-2">
        <Users2 className="w-6 h-6 text-[var(--tr-deep-green)]" />
        <h2 className="tr-section-title text-3xl">{t("admin_p2.interests_title")}</h2>
      </div>
      <p className="text-[var(--tr-muted)] leading-relaxed max-w-3xl">{t("admin_p2.interests_body")}</p>

      {/* Status filter */}
      <div className="mt-6 flex flex-wrap gap-2">
        {[
          { key: "", label: t("admin_p2.filter_all") },
          { key: "new", label: "New" },
          { key: "contacted", label: "Contacted" },
          { key: "converted", label: t("admin_p2.converted") },
          { key: "rejected", label: "Rejected" },
        ].map((s) => (
          <button
            key={s.key || "all"}
            data-testid={`interest-filter-${s.key || "all"}`}
            onClick={() => setStatus(s.key)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${
              status === s.key
                ? "bg-[var(--tr-deep-green)] text-white"
                : "bg-white border border-[rgba(6,78,59,0.15)] text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {!items ? (
        <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div data-testid="fi-empty" className="tr-card p-10 text-center text-[var(--tr-muted)] mt-6">
          {t("admin_p2.empty_interests")}
        </div>
      ) : (
        <div className="tr-card overflow-hidden mt-6" data-testid="fi-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--tr-sage)]/50">
                <tr>
                  {[t("admin.col_name"), t("admin.col_phone"), "Village", t("admin_p2.col_interest"), t("admin_p2.col_status"), t("admin_p2.col_created")].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>
                  ))}
                  <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} data-testid={`fi-row-${it.id}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)] align-top">
                    <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{it.full_name}</td>
                    <td className="px-4 py-3 font-mono">{it.phone}</td>
                    <td className="px-4 py-3">{it.village}{it.taluka ? `, ${it.taluka}` : ""}</td>
                    <td className="px-4 py-3 text-xs">{it.interest_topic ? t(`farmer_interest.${it.interest_topic}`, it.interest_topic) : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_TONES[it.status] || "bg-gray-100 text-gray-700"}`}>
                        {it.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--tr-muted)]">{new Date(it.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {it.status !== "converted" && (
                        <>
                          {it.status === "new" && (
                            <button data-testid={`fi-contacted-${it.id}`} onClick={() => markContacted(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] text-sm font-semibold mr-3 hover:underline">
                              <PhoneCall className="w-4 h-4" /> {t("admin_p2.action_mark_contacted")}
                            </button>
                          )}
                          <button data-testid={`fi-convert-${it.id}`} onClick={() => convert(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline">
                            <ArrowRight className="w-4 h-4" /> {t("admin_p2.action_convert")}
                          </button>
                        </>
                      )}
                      {it.status === "converted" && it.converted_profile_id && (
                        <span className="text-xs text-[var(--tr-muted)] mr-3">→ profile linked</span>
                      )}
                      {it.status !== "rejected" && it.status !== "converted" && (
                        <button data-testid={`fi-reject-${it.id}`} onClick={() => reject(it.id)} className="inline-flex items-center gap-1 text-red-600 text-sm font-semibold mr-3 hover:underline">
                          {t("admin_p2.action_reject")}
                        </button>
                      )}
                      <button data-testid={`fi-delete-${it.id}`} onClick={() => remove(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-muted)] hover:text-red-700 text-sm font-semibold">
                        <Trash2 className="w-4 h-4" /> {t("common.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
